import { ashleyLog, ashleyWarn } from "../debug/ashleySttDebug";
import { msSinceLastGesture, voiceDiag } from "../debug/ashleyVoiceDiagnostics";

const ASHLEY_VOICE_NAME = "Google UK English Female";
const SPEAKING_POLL_INTERVAL_MS = 200;
const FALLBACK_MIN_MS = 1200;
const FALLBACK_MS_PER_CHAR = 55;
const START_WATCHDOG_MS = 1000;
const UNLOCK_UTTERANCE_TEXT = ".";

/**
 * iOS Safari / WKWebView silently no-ops speechSynthesis.speak() until the first
 * speak() call happens inside a genuine user gesture (WebKit
 * RequireUserGestureForSpeechStart). Once cleared for the document, later
 * programmatic speaks work for the page session — including after async gaps.
 * @type {boolean}
 */
let speechUnlockedForDocument = false;

const IOS_ENGLISH_VOICE_PATTERNS = [
  /samantha/i,
  /karen/i,
  /moira/i,
  /tessa/i,
  /fiona/i,
  /victoria/i,
  /susan/i,
];

/**
 * Production-safe warning: error codes and sources only, never utterance text.
 * @param {"speak-threw" | "utterance-error" | "never-started"} kind
 * @param {Record<string, string | boolean | null | undefined>} [details]
 */
function reportAshleySpeechIssue(kind, details = {}) {
  console.warn("[Ashley speech]", kind, details);
}

/**
 * @param {Array<{ name?: string, lang?: string, localService?: boolean }>} [voices]
 * @returns {{ name?: string, lang?: string, localService?: boolean } | null}
 */
export function selectAshleyVoice(voices = []) {
  const list = Array.isArray(voices) ? voices : [];
  const preferred = list.find((voice) => voice.name === ASHLEY_VOICE_NAME);
  if (preferred) {
    return preferred;
  }

  const englishVoices = list.filter((voice) =>
    /^en([-_]|$)/i.test(voice.lang || ""),
  );

  for (const pattern of IOS_ENGLISH_VOICE_PATTERNS) {
    const match = englishVoices.find((voice) => pattern.test(voice.name || ""));
    if (match) {
      return match;
    }
  }

  return (
    englishVoices.find((voice) => voice.localService) ||
    englishVoices[0] ||
    null
  );
}

function findAshleyVoice() {
  if (!window.speechSynthesis) {
    return null;
  }

  return selectAshleyVoice(window.speechSynthesis.getVoices());
}

function estimateTtsDurationMs(text) {
  return Math.max(FALLBACK_MIN_MS, text.length * FALLBACK_MS_PER_CHAR);
}

function primeSpeechSynthesis() {
  if (!window.speechSynthesis) {
    return;
  }

  try {
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
    }
  } catch (error) {
    ashleyWarn("speechSynthesis.resume() failed", {
      message: error?.message || String(error),
    });
  }
}

export function isAshleySpeechSupported() {
  return (
    typeof window !== "undefined" &&
    Boolean(window.speechSynthesis) &&
    typeof window.speechSynthesis.speak === "function"
  );
}

export function isAshleySpeechUnlocked() {
  return speechUnlockedForDocument;
}

/**
 * Unlock WebKit speech synthesis during a synchronous user gesture.
 * Must be called from the FAB tap/long-press stack before any await/timer.
 * Safe no-op on Android / desktop Chrome (no gesture restriction).
 *
 * Repeated calls cancel leftover utterances first so silent priming cannot queue.
 * @returns {boolean} true when speak() was invoked for unlock
 */
export function unlockAshleySpeech() {
  if (!isAshleySpeechSupported()) {
    voiceDiag("Speech unlock skipped — speechSynthesis unsupported", {
      hypothesisId: "H-IOS-TTS",
      location: "ashleySpeech.unlockAshleySpeech",
    });
    return false;
  }

  try {
    window.speechSynthesis.getVoices();
    primeSpeechSynthesis();

    if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
      window.speechSynthesis.cancel();
    }

    const unlockUtterance = new SpeechSynthesisUtterance(UNLOCK_UTTERANCE_TEXT);
    unlockUtterance.volume = 0;
    unlockUtterance.rate = 10;
    unlockUtterance.pitch = 1;
    unlockUtterance.lang = "en-US";

    window.speechSynthesis.speak(unlockUtterance);
    speechUnlockedForDocument = true;

    voiceDiag("Speech unlock speak() issued (iOS gesture gate)", {
      hypothesisId: "H-IOS-TTS",
      location: "ashleySpeech.unlockAshleySpeech",
      data: {
        msSinceGesture: msSinceLastGesture(),
        voiceCount: window.speechSynthesis.getVoices().length,
      },
    });
    return true;
  } catch (error) {
    ashleyWarn("Speech unlock failed", {
      message: error?.message || String(error),
    });
    voiceDiag(`Speech unlock threw: ${error?.message || String(error)}`, {
      hypothesisId: "H-IOS-TTS",
      location: "ashleySpeech.unlockAshleySpeech.catch",
    });
    reportAshleySpeechIssue("speak-threw", {
      source: "unlock",
      message: error?.message || String(error),
    });
    return false;
  }
}

/** @internal test helper */
export function resetAshleySpeechUnlockForTests() {
  speechUnlockedForDocument = false;
}

export function cancelAshleySpeech() {
  if (isAshleySpeechSupported()) {
    window.speechSynthesis.cancel();
  }
}

/**
 * Speak text using Ashley's voice profile.
 * @param {string} text
 * @param {{
 *   onEnd?: (source: string) => void,
 *   onError?: (event: SpeechSynthesisErrorEvent) => void,
 *   onStart?: (source: string) => void,
 * }} [options]
 */
export function speakAshley(text, options = {}) {
  const { onEnd, onError, onStart } = options;

  voiceDiag("CHAIN 2/8: speakAshley() entered", {
    hypothesisId: "H-CHAIN",
    location: "ashleySpeech.speakAshley",
    data: {
      textLength: text.length,
      voiceCount: isAshleySpeechSupported()
        ? window.speechSynthesis.getVoices().length
        : 0,
      msSinceGesture: msSinceLastGesture(),
      speechUnlocked: speechUnlockedForDocument,
    },
  });

  if (!isAshleySpeechSupported()) {
    ashleyWarn("TTS skipped — speechSynthesis not supported");
    voiceDiag("TTS unsupported (speechSynthesis missing)", {
      hypothesisId: "H-G",
      location: "ashleySpeech.speakAshley",
    });
    onEnd?.("unsupported");
    return;
  }

  if (!speechUnlockedForDocument) {
    voiceDiag(
      "TTS speak without prior unlock — iOS may silently drop this utterance",
      {
        hypothesisId: "H-IOS-TTS",
        location: "ashleySpeech.speakAshley",
        data: { msSinceGesture: msSinceLastGesture() },
      },
    );
  }

  let finished = false;
  let pollTimer = null;
  let fallbackTimer = null;
  let startWatchdogTimer = null;
  let playbackStarted = false;

  const clearTimers = () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    if (fallbackTimer) {
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
    if (startWatchdogTimer) {
      clearTimeout(startWatchdogTimer);
      startWatchdogTimer = null;
    }
  };

  const markPlaybackStarted = (source) => {
    if (playbackStarted || finished) {
      return;
    }

    playbackStarted = true;
    if (startWatchdogTimer) {
      clearTimeout(startWatchdogTimer);
      startWatchdogTimer = null;
    }

    ashleyLog("TTS playback confirmed", { source });
    voiceDiag("TTS playback confirmed", {
      hypothesisId: "H-C",
      location: "ashleySpeech.speakAshley.markPlaybackStarted",
      data: { source, msSinceGesture: msSinceLastGesture() },
    });
    onStart?.(source);
  };

  const finishOnce = (source) => {
    if (finished) {
      return;
    }

    const tryComplete = () => {
      if (finished) {
        return;
      }

      if (isAshleySpeechSupported()) {
        const { speaking, pending } = window.speechSynthesis;
        if (speaking || pending) {
          markPlaybackStarted("poll-speaking-true");
          setTimeout(tryComplete, SPEAKING_POLL_INTERVAL_MS);
          return;
        }
      }

      finished = true;
      clearTimers();

      if (!playbackStarted && source === "timeout-fallback-never-spoke") {
        reportAshleySpeechIssue("never-started", { source });
      }

      ashleyLog("TTS finished", { source, playbackStarted });
      voiceDiag("CHAIN 4/8: finishOnce()", {
        hypothesisId: "H-CHAIN",
        location: "ashleySpeech.speakAshley.finishOnce",
        data: {
          source,
          playbackStarted,
          msSinceGesture: msSinceLastGesture(),
          speechUnlocked: speechUnlockedForDocument,
        },
      });
      onEnd?.(source);
    };

    tryComplete();
  };

  const utterance = new SpeechSynthesisUtterance(text);
  const ashleyVoice = findAshleyVoice();
  const voices = window.speechSynthesis.getVoices();

  voiceDiag("TTS speak() executing", {
    hypothesisId: "H-G",
    location: "ashleySpeech.speakAshley.speak",
    data: {
      voiceCount: voices.length,
      ashleyVoiceFound: Boolean(ashleyVoice),
      ashleyVoiceName: ashleyVoice?.name || null,
      ashleyVoiceLang: ashleyVoice?.lang || null,
      msSinceGesture: msSinceLastGesture(),
      speechUnlocked: speechUnlockedForDocument,
    },
  });

  if (ashleyVoice) {
    utterance.voice = ashleyVoice;
  }

  utterance.rate = 1;
  utterance.pitch = 0.85;
  utterance.volume = 1;

  utterance.onstart = () => {
    markPlaybackStarted("utterance.onstart");
  };

  utterance.onend = () => {
    if (playbackStarted) {
      finishOnce("utterance.onend");
      return;
    }

    finishOnce("timeout-fallback-never-spoke");
  };

  utterance.onerror = (event) => {
    const errorCode = event?.error || "unknown";
    ashleyWarn("TTS error", { error: errorCode });
    voiceDiag(`TTS utterance.onerror: ${errorCode}`, {
      hypothesisId: "H-C",
      location: "ashleySpeech.speakAshley.onerror",
      data: { error: errorCode },
    });
    reportAshleySpeechIssue("utterance-error", {
      error: errorCode,
      playbackStarted,
    });
    onError?.(event);
    finishOnce(`utterance.onerror:${errorCode}`);
  };

  primeSpeechSynthesis();

  if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
    window.speechSynthesis.cancel();
  }

  voiceDiag("CHAIN 3/8: speechSynthesis.speak() called", {
    hypothesisId: "H-CHAIN",
    location: "ashleySpeech.speakAshley.speak",
    data: { msSinceGesture: msSinceLastGesture() },
  });

  try {
    window.speechSynthesis.speak(utterance);
  } catch (error) {
    ashleyWarn("speechSynthesis.speak() threw", {
      message: error?.message || String(error),
    });
    voiceDiag(`TTS speak() threw: ${error?.message || String(error)}`, {
      hypothesisId: "H-C",
      location: "ashleySpeech.speakAshley.speak.catch",
    });
    reportAshleySpeechIssue("speak-threw", {
      message: error?.message || String(error),
    });
    finishOnce("speak-threw");
    return;
  }

  startWatchdogTimer = setTimeout(() => {
    if (playbackStarted || finished) {
      return;
    }

    const { speaking, pending } = window.speechSynthesis;
    if (speaking || pending) {
      markPlaybackStarted("watchdog-speaking-flag");
      return;
    }

    finishOnce("timeout-fallback-never-spoke");
  }, START_WATCHDOG_MS);

  const scheduleFallbackCheck = (delayMs = estimateTtsDurationMs(text)) => {
    fallbackTimer = setTimeout(() => {
      const { speaking, pending } = window.speechSynthesis;
      if (speaking || pending) {
        markPlaybackStarted("poll-speaking-true");
        voiceDiag("TTS timeout fallback waiting for speech idle", {
          hypothesisId: "H-C",
          location: "ashleySpeech.speakAshley.fallbackTimeout",
        });
        scheduleFallbackCheck(SPEAKING_POLL_INTERVAL_MS);
        return;
      }

      voiceDiag("TTS timeout fallback firing", {
        hypothesisId: "H-C",
        location: "ashleySpeech.speakAshley.fallbackTimeout",
        data: {
          speaking,
          pending,
          playbackStarted,
        },
      });
      finishOnce(
        playbackStarted
          ? "timeout-fallback-after-idle"
          : "timeout-fallback-never-spoke",
      );
    }, delayMs);
  };

  scheduleFallbackCheck();

  pollTimer = setInterval(() => {
    const { speaking, pending } = window.speechSynthesis;
    if (speaking || pending) {
      markPlaybackStarted("poll-speaking-true");
      return;
    }

    if (playbackStarted) {
      voiceDiag("TTS poll detected speech ended", {
        hypothesisId: "H-C",
        location: "ashleySpeech.speakAshley.poll",
      });
      finishOnce("poll-speaking-false");
    }
  }, SPEAKING_POLL_INTERVAL_MS);
}
